import { useEffect, useState, useMemo } from "react";
import { Mail, RefreshCw, Eye, FileText, Loader2, Search, Send, Check, X } from "lucide-react";
import api from "../../lib/api";

/**
 * Sprint EMAIL-X · Phase 2.5 — super_admin-only Email Template Preview.
 *
 * Lets the brand team browse the entire transactional email library,
 * preview any template in an iframe (sandboxed for safety), and toggle
 * between HTML and plain-text fallback.  Calls the new
 * GET /api/admin/email/preview endpoints — no real emails are sent.
 */
export default function EmailTemplatePreviewPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedKey, setSelectedKey] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [search, setSearch] = useState("");
  const [textMode, setTextMode] = useState(false);
  const [sending, setSending] = useState(false);
  // Toast: { tone: "success" | "error", text: string }
  const [toast, setToast] = useState(null);

  // Auto-dismiss the toast after 4s.
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleSendTest = async () => {
    if (!selectedKey || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(
        `/admin/email/preview/${selectedKey}/send-me-test`,
      );
      if (data?.result === "sent") {
        setToast({
          tone: "success",
          text: `Test email sent to ${data.recipient_email}`,
        });
      } else {
        setToast({
          tone: "error",
          text: `Send failed: ${data?.error || "Unknown error"}`,
        });
      }
    } catch (e) {
      setToast({
        tone: "error",
        text: e?.response?.data?.detail || e.message || "Send failed",
      });
    } finally {
      setSending(false);
    }
  };

  // Load the list of registered templates on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/email/preview");
        if (!cancelled) {
          const sorted = [...data].sort((a, b) => a.key.localeCompare(b.key));
          setTemplates(sorted);
          if (sorted.length > 0) {
            setSelectedKey(sorted[0].key);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Refresh preview whenever the selected key changes.
  useEffect(() => {
    if (!selectedKey) return undefined;
    let cancelled = false;
    setPreviewing(true);
    (async () => {
      try {
        const { data } = await api.get(`/admin/email/preview/${selectedKey}`);
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedKey]);

  const filtered = useMemo(() => {
    if (!search.trim()) return templates;
    const needle = search.trim().toLowerCase();
    return templates.filter(
      (t) => t.key.toLowerCase().includes(needle) || (t.category || "").toLowerCase().includes(needle),
    );
  }, [templates, search]);

  return (
    <div className="min-h-full bg-[#F4F1EA]" data-testid="email-preview-page">
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37] font-semibold mb-2">
              Super Admin · EMAIL-X
            </p>
            <h1
              className="font-serif text-4xl text-[#0A192F] leading-tight"
              style={{ fontFamily: '"Cormorant Garamond", serif' }}
            >
              Email Template <i className="text-[#D4AF37]">Preview</i>
            </h1>
            <p className="text-sm text-slate-600 mt-3 max-w-2xl">
              Browse and preview every Diplomexa transactional email against safe sample data.
              No real emails are sent — caller PII never echoes into these previews.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span
              data-testid="email-preview-template-count"
              className="px-3 py-1.5 text-xs rounded-full bg-white border border-[#0A192F]/15 text-[#0A192F]"
            >
              {templates.length} templates registered
            </span>
          </div>
        </div>

        {error ? (
          <div
            data-testid="email-preview-error"
            className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-4 py-3 text-sm mb-6"
          >
            {String(error)}
          </div>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: list + search */}
          <div className="lg:col-span-4 bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col">
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="email-preview-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by key or category…"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-sm"
              />
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm py-8 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading templates…
              </div>
            ) : (
              <ul
                data-testid="email-preview-template-list"
                className="space-y-1 max-h-[70vh] overflow-y-auto pr-1"
              >
                {filtered.map((t) => {
                  const isActive = t.key === selectedKey;
                  return (
                    <li key={t.key}>
                      <button
                        type="button"
                        data-testid={`email-preview-template-${t.key}`}
                        onClick={() => setSelectedKey(t.key)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex flex-col gap-0.5 ${
                          isActive
                            ? "bg-[#0A192F] text-white"
                            : "hover:bg-slate-100 text-slate-700"
                        }`}
                      >
                        <span className="font-mono text-xs truncate">{t.key}</span>
                        <span
                          className={`text-[10px] uppercase tracking-[0.18em] ${
                            isActive ? "text-[#D4AF37]" : "text-slate-500"
                          }`}
                        >
                          {t.category || "Diplomexa"}
                        </span>
                      </button>
                    </li>
                  );
                })}
                {filtered.length === 0 && !loading ? (
                  <li className="text-center text-xs text-slate-400 py-6">No matches.</li>
                ) : null}
              </ul>
            )}
          </div>

          {/* Right: preview */}
          <div className="lg:col-span-8 bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            {previewing && !preview ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm py-12 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Rendering preview…
              </div>
            ) : preview ? (
              <>
                <div className="border-b border-slate-200 pb-3 mb-4">
                  <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37] font-semibold">
                    {preview.category}
                  </p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <Mail className="w-4 h-4 text-[#0A192F] shrink-0" />
                    <h2
                      data-testid="email-preview-subject"
                      className="text-lg font-medium text-[#0A192F]"
                    >
                      {preview.subject}
                    </h2>
                  </div>
                  {preview.preheader ? (
                    <p
                      data-testid="email-preview-preheader"
                      className="text-xs text-slate-500 mt-1"
                    >
                      {preview.preheader}
                    </p>
                  ) : null}
                  <p
                    data-testid="email-preview-key"
                    className="text-[11px] font-mono text-slate-400 mt-2"
                  >
                    {preview.key}
                  </p>
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <button
                    type="button"
                    data-testid="email-preview-html-tab"
                    onClick={() => setTextMode(false)}
                    className={`px-3 py-1.5 text-xs rounded-md ${
                      !textMode
                        ? "bg-[#0A192F] text-[#D4AF37]"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    <Eye className="w-3.5 h-3.5 inline mr-1" /> HTML preview
                  </button>
                  <button
                    type="button"
                    data-testid="email-preview-text-tab"
                    onClick={() => setTextMode(true)}
                    className={`px-3 py-1.5 text-xs rounded-md ${
                      textMode
                        ? "bg-[#0A192F] text-[#D4AF37]"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    <FileText className="w-3.5 h-3.5 inline mr-1" /> Plain text
                  </button>
                  <button
                    type="button"
                    data-testid="email-preview-refresh"
                    onClick={() => setSelectedKey((k) => k && `${k}`)}
                    className="ml-auto px-3 py-1.5 text-xs rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200"
                    title="Re-render"
                  >
                    <RefreshCw className="w-3.5 h-3.5 inline mr-1" /> Re-render
                  </button>
                  <button
                    type="button"
                    data-testid="email-preview-send-me-test"
                    onClick={handleSendTest}
                    disabled={sending || !selectedKey}
                    className="px-3 py-1.5 text-xs rounded-md bg-[#D4AF37] text-[#0A192F] font-semibold hover:bg-[#E5C158] disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Send the rendered preview to your own email"
                  >
                    {sending ? (
                      <Loader2 className="w-3.5 h-3.5 inline mr-1 animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5 inline mr-1" />
                    )}
                    Send me a test
                  </button>
                </div>

                {textMode ? (
                  <pre
                    data-testid="email-preview-text-content"
                    className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-700 whitespace-pre-wrap font-mono max-h-[65vh] overflow-y-auto"
                  >
                    {preview.text}
                  </pre>
                ) : (
                  <iframe
                    data-testid="email-preview-iframe"
                    title={`Email preview — ${preview.key}`}
                    srcDoc={preview.html}
                    sandbox=""
                    className="w-full h-[70vh] rounded-lg border border-slate-200 bg-white"
                  />
                )}
              </>
            ) : (
              <div className="text-center text-sm text-slate-400 py-12">
                Pick a template on the left to preview.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast ? (
        <div
          data-testid="email-preview-toast"
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border ${
            toast.tone === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-rose-50 border-rose-200 text-rose-900"
          }`}
        >
          {toast.tone === "success" ? (
            <Check className="w-4 h-4 text-emerald-600" />
          ) : (
            <X className="w-4 h-4 text-rose-600" />
          )}
          <span className="text-sm font-medium">{toast.text}</span>
        </div>
      ) : null}
    </div>
  );
}
